/*
 * Copyright (c) 2023 Irian Software Development SRL. All Rights Reserved.
 * This software is the proprietary information of Irian Software Development SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Injectable } from '@angular/core';
import { LocalStorageService, SessionStorageService } from 'ngx-webstorage';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { cookieTokenName, localStorageTokenName, UserContext } from '@irian-ro/util-user';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {EnvironmentService} from "@irian-ro/util-environment";
import { LoginDetails } from './login-details.model';
import { TermsAndConditionsRequestService } from '../terms-and-conditions/terms-and-conditions-request.service';
import { Cookies } from 'typescript-cookie';

@Injectable()
export class LoginService {
  constructor(
    private readonly http: HttpClient,
    private readonly $localStorage: LocalStorageService,
    private readonly $sessionStorage: SessionStorageService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly userContext: UserContext,
    private readonly environmentService: EnvironmentService,
    private readonly termsAndConditionsRequestService: TermsAndConditionsRequestService
  ) {}

  /**
   * Calls the server to authenticate the current user and on success stores the jwt received from the server
   * @param loginDetails username and password
   * @returns
   */
  login(loginDetails: LoginDetails): Observable<string> {
    return this.http.post('api/authenticate', loginDetails).pipe(
      map((resp) => {
        const bearerToken = resp['id_token'];
        if (bearerToken) {
          const jwt = bearerToken;
          this.storeAuthenticationToken(jwt, true);
          this.storeAccessTokenInCookie(jwt);
          return jwt;
        }
      })
    );
  }

  doLogin(loginDetails: LoginDetails): Observable<string> {
    this.$localStorage.clear();

    const loginUrl = this.environmentService.getEnvironmentProperty('loginUrl');
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
        'Access-Control-Allow-Origin': '*',
      }),
    };

    const body = new URLSearchParams();
    body.set('username', loginDetails.username);
    body.set('password', loginDetails.password);
    body.set('grant_type', 'password');

    return this.http.post(loginUrl, body.toString(), httpOptions).pipe(
      map((response) => {
        this.termsAndConditionsRequestService.emitTermsAndConditionsRequest();
        const bearerToken = response['access_token'];
        const owner = response['owner'];
        if (bearerToken) {
          const jwt = bearerToken;
          this.storeAuthenticationToken(jwt, true);
          this.storeOwner(owner, true);
          this.storeAccessTokenInCookie(jwt);
          this.storeOrganization(owner);
          return jwt;
        }
      })
    );
  }

  storeAccessTokenInCookie(jwt: string) {
    Cookies?.set(cookieTokenName, jwt);
  }

  clearAccessTokenCookie() {
    Cookies?.remove(cookieTokenName);
  }

  storeOrganization(jwt: string) {
    Cookies?.set('organization', jwt);
  }

  clearOrganization() {
    Cookies?.remove('organization');
  }

  storeAuthenticationToken(jwt: string, rememberMe: boolean) {
    if (rememberMe) {
      this.$localStorage.store(localStorageTokenName, jwt);
    } else {
      this.$sessionStorage.store(localStorageTokenName, jwt);
    }
  }

  storeOwner(owner: string, rememberMe: boolean) {
    if (rememberMe) {
      this.$localStorage.store('owner', owner);
    } else {
      this.$sessionStorage.store('owner', owner);
    }
  }

  logout(redirectToLogin = true): Observable<void> {
    return new Observable((observer) => {
      this.$localStorage.clear();
      this.$sessionStorage.clear();
      this.clearAccessTokenCookie();
      this.clearOrganization();
      this.termsAndConditionsRequestService.unsubscribe();
      this.userContext.logout(redirectToLogin);
      observer.complete();
    });
  }

  navigateToProfile() {
    this.userContext.getLoggedInUser().subscribe((currentUser: any) => {
      if (currentUser) {
        this.router.navigate(['../auth/profile', currentUser.uuid], {
          relativeTo: this.route,
        });
      } else {
        this.router.navigate(['../auth/login'], {
          relativeTo: this.route,
        });
      }
    });
  }
}
